import type { UploadResult } from '../types';

// Default configuration - can be overridden via env vars
const DEFAULT_ENDPOINT = (import.meta as any).env?.VITE_UPLOAD_ENDPOINT || "https://asci-gcs-uploader-922405893209.asia-south1.run.app";
const DEFAULT_BUCKET = (import.meta as any).env?.VITE_GCS_BUCKET || "asci-uploads-bucket";
const DEFAULT_FOLDER = (import.meta as any).env?.VITE_GCS_FOLDER || "cfd-simulations";

/**
 * Uploads a file to GCS via signed URL
 * @param file The File object to upload
 * @param folder Optional folder path inside the bucket
 * @returns UploadResult with GCS URL
 */
export async function uploadFileToGCS(
  file: File,
  folder: string = DEFAULT_FOLDER,
): Promise<UploadResult> {
  if (!file) throw new Error("File is required");

  // Request signed URL from Cloud Run
  const res = await fetch(DEFAULT_ENDPOINT + "/signUpload", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      bucket: DEFAULT_BUCKET,
      folder,
      filename: file.name,
      content_type: file.type,
    }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to get signed URL: ${text}`);
  }

  const { upload_url, bucket, object } = await res.json();

  // Upload file to GCS using the signed URL
  const uploadRes = await fetch(upload_url, {
    method: "PUT",
    headers: {
      "Content-Type": file.type,
    },
    body: file,
  });

  if (!uploadRes.ok) {
    const text = await uploadRes.text();
    throw new Error(`Failed to upload file to GCS: ${text}`);
  }

  const gcsUrl = `gs://${bucket}/${object}`;
  return { gcsUrl, bucket, object };
}

/**
 * Verify if a TIFF file is Cloud Optimized (COG)
 * @param fileUrl GCS URL of the file
 * @param tileServerHost Tile server endpoint
 * @returns Verification result with status message
 */
export async function verifyCOG(
  fileUrl: string,
  tileServerHost: string = "https://tile-server.asci.co.in"
): Promise<{ isCOG: boolean; message: string }> {
  try {
    const infoUrl = `${tileServerHost}/info?url=${encodeURIComponent(fileUrl)}`;
    const response = await fetch(infoUrl);
    
    if (!response.ok) {
      return { isCOG: false, message: 'Failed to verify file' };
    }
    
    const info = await response.json();
    
    const hasOverviews = info.overviews && info.overviews.length > 0;
    const isTiled = info.driver === 'GTiff';
    
    if (hasOverviews || isTiled) {
      return { isCOG: true, message: '✓ COG verified - optimized for streaming' };
    } else {
      return { isCOG: false, message: '⚠ Not a COG - may load slowly' };
    }
  } catch (error) {
    console.error('COG verification error:', error);
    return { isCOG: false, message: '⚠ Could not verify COG status' };
  }
}
